import React, { useEffect, useMemo, useState } from 'react';
import { useLMS } from '../../context/LMSContext';
import { authHelpers, db } from '../../lib/supabase';
import { generateThinkificReportPdf } from '../../lib/thinkificReportPdf';
import { endOfLocalDayISO } from '../../lib/dates';

/**
 * ThinkificEnrollment
 * -----------------------------------------------------------------------
 * Admin-only page for granting and revoking student access to the
 * Thinkific NCLEX Review Course. The Thinkific API token and course ID
 * live exclusively on the server (Netlify function); this component just
 * triggers the proxied actions and reflects the cached status stored on
 * each student's profile.
 *
 * Restricted to currentUser.role === 'admin'. App.jsx and Sidebar.jsx
 * also gate the route, but this component re-checks defensively so it
 * is safe even if those gates are ever loosened.
 * -----------------------------------------------------------------------
 */
const ThinkificEnrollment = () => {
  const { currentUser, students, cohorts, refreshStudents } = useLMS();

  const [searchTerm, setSearchTerm] = useState('');
  const [filterCohort, setFilterCohort] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPayment, setFilterPayment] = useState('all'); // 'all' | 'paid' | 'free'

  const [busyStudentId, setBusyStudentId] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [syncBusy, setSyncBusy] = useState(false);

  const [confirm, setConfirm] = useState(null); // { type, student?, cohortId? }
  const [bulkResult, setBulkResult] = useState(null);
  const [toast, setToast] = useState(null); // { kind: 'success'|'error', message }

  // Expiration picker state for grant flows. Default 6 weeks (matches
  // the cohort length). 'none' means no expiry (Thinkific = unlimited).
  // 'custom' opens a date input for an arbitrary expiry date.
  const [grantDuration, setGrantDuration] = useState('42');
  const [grantCustomDate, setGrantCustomDate] = useState('');

  // Paid backfill (Netlify background function) state.
  const [backfillBusy, setBackfillBusy] = useState(false);
  const [confirmBackfill, setConfirmBackfill] = useState(false);

  // Add Thinkific Student modal state.
  // Used for ad-hoc enrollments of people who aren't part of any cohort.
  const [showAddModal, setShowAddModal] = useState(false);
  const [addBusy, setAddBusy] = useState(false);
  const [addForm, setAddForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    duration: '42',
    customDate: '',
  });

  // Bulk Import modal state. Mirrors the 4-step flow (input → preview →
  // importing → done) used in UserManagement's bulk import so admins
  // have a familiar UX. Drives the bulk_enroll_by_email action.
  const [showBulkImport, setShowBulkImport] = useState(false);
  const [bulkImportStep, setBulkImportStep] = useState('input'); // 'input' | 'preview' | 'importing' | 'done'
  const [bulkImportText, setBulkImportText] = useState('');
  const [bulkImportParsed, setBulkImportParsed] = useState([]);
  const [bulkImportResults, setBulkImportResults] = useState([]);
  const [bulkImportDuration, setBulkImportDuration] = useState('42');
  const [bulkImportCustomDate, setBulkImportCustomDate] = useState('');
  const [bulkImportBusy, setBulkImportBusy] = useState(false);
  const BULK_IMPORT_MAX = 100;

  const [showAuditLog, setShowAuditLog] = useState(false);
  const [auditEntries, setAuditEntries] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);

  const [detailsStudent, setDetailsStudent] = useState(null);

  // Progress Report state.
  // - reportBusy is the id of the student whose row report is running, or
  //   the sentinel '__group__' for a multi-student / cohort run.
  // - reportProgress is { current, total, label } shown in an overlay so
  //   the admin sees which student is being fetched as the run proceeds.
  const [reportBusy, setReportBusy] = useState(null);
  const [reportProgress, setReportProgress] = useState(null);
  const [reportCancel, setReportCancel] = useState({ cancelled: false });

  // Thinkific NCLEX students who have no LMS profile (legacy direct-Thinkific
  // signups, etc). Loaded from thinkific_unlinked_enrollments. Populated by
  // sync and webhooks server-side.
  const [unlinked, setUnlinked] = useState([]);

  const isAdmin = currentUser?.role === 'admin';

  // Load unlinked enrollments on mount and after every sync/grant/revoke.
  const refreshUnlinked = async () => {
    const { data } = await db.getThinkificUnlinkedEnrollments();
    setUnlinked(data || []);
  };

  useEffect(() => {
    if (isAdmin) refreshUnlinked();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  // Convert an unlinked row into the same shape as a normalized LMS student
  // so the table can render both kinds with the same code path. Synthetic
  // id ('unlinked:<thinkificUserId>') keeps React keys unique.
  const unlinkedAsStudent = (u) => ({
    id: `unlinked:${u.thinkific_user_id}`,
    email: u.email,
    name: u.full_name || u.email,
    role: 'student',
    cohortId: null,
    thinkific_user_id: u.thinkific_user_id,
    thinkific_enrollment_id: u.thinkific_enrollment_id,
    thinkific_status: u.thinkific_status,
    thinkific_synced_at: u.thinkific_synced_at,
    thinkific_percentage_completed: u.thinkific_percentage_completed,
    thinkific_started_at: u.thinkific_started_at,
    thinkific_completed_at: u.thinkific_completed_at,
    thinkific_activated_at: u.thinkific_activated_at,
    thinkific_expiry_date: u.thinkific_expiry_date,
    thinkific_completed: u.thinkific_completed,
    thinkific_last_signin_at: u.thinkific_last_signin_at,
    thinkific_paid: u.thinkific_paid ?? false,
    thinkific_first_paid_at: u.thinkific_first_paid_at ?? null,
    _isThinkificOnly: true,
  });

  const allStudents = useMemo(() => {
    const linked = (students || []).filter((s) => s.role === 'student');
    const unlinkedNormalized = (unlinked || []).map(unlinkedAsStudent);
    return [...linked, ...unlinkedNormalized];
  }, [students, unlinked]);

  const cohortLookup = useMemo(() => {
    const map = new Map();
    (cohorts || []).forEach((c) => map.set(c.id, c.name));
    return map;
  }, [cohorts]);

  // The stored thinkific_status goes stale when an expiry passes without a
  // webhook, so active/expired is derived from thinkific_expiry_date
  // (null or in the future = active). 'never' and unlinked are kept as-is.
  const effectiveStatus = (s) => {
    if (!s.thinkific_status || s.thinkific_status === 'never') return s.thinkific_status || null;
    return !s.thinkific_expiry_date || new Date(s.thinkific_expiry_date) > new Date()
      ? 'active'
      : 'expired';
  };

  const statusLabel = (s) => {
    const st = effectiveStatus(s);
    if (st === 'active') return 'Has Access';
    if (st === 'expired') return 'Access Expired';
    if (st === 'never') return 'No Access';
    return 'Not Linked';
  };

  const statusKey = (s) => effectiveStatus(s) || 'unlinked';

  const filtered = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    return allStudents.filter((s) => {
      if (q) {
        const hay = `${s.name || ''} ${s.email || ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (filterCohort !== 'all') {
        if (filterCohort === 'thinkific-only') {
          if (!s._isThinkificOnly) return false;
        } else if (filterCohort === 'unassigned') {
          // Only LMS students with no cohort - exclude Thinkific-only rows
          if (s._isThinkificOnly || s.cohortId) return false;
        } else {
          if (s.cohortId !== filterCohort) return false;
        }
      }
      if (filterStatus !== 'all') {
        if (filterStatus === 'unlinked') {
          if (s.thinkific_status) return false;
        } else if (effectiveStatus(s) !== filterStatus) {
          return false;
        }
      }
      if (filterPayment !== 'all') {
        if (filterPayment === 'paid' && !s.thinkific_paid) return false;
        if (filterPayment === 'free' && s.thinkific_paid) return false;
      }
      return true;
    });
  }, [allStudents, searchTerm, filterCohort, filterStatus, filterPayment]);

  const counts = useMemo(() => {
    const c = {
      active: 0, expired: 0, never: 0, unlinked: 0, thinkific_only: 0,
      paid: 0, free: 0,
    };
    for (const s of allStudents) {
      if (s._isThinkificOnly) c.thinkific_only++;
      const k = statusKey(s);
      if (c[k] !== undefined) c[k]++;
      if (s.thinkific_paid) c.paid++;
      else c.free++;
    }
    return c;
  }, [allStudents]);

  const showToast = (kind, message) => {
    setToast({ kind, message });
    setTimeout(() => setToast(null), 4500);
  };

  // Opens the admin's default email client with a pre-filled message
  // containing the Stripe payment link. The admin reviews, edits if
  // they want, and clicks send. No backend involvement - the email
  // sends from the admin's own account so replies route there.
  const emailExtensionLink = (student) => {
    const firstName = (student.name || '').trim().split(/\s+/)[0] || 'there';
    const subject = 'Extend your NCLEX Review Course access';
    const body =
      `Hi ${firstName},\n\n` +
      `To extend your NCLEX Review Course access by 45 days, click the link below to complete the secure payment through Stripe:\n\n` +
      `${STRIPE_EXTENSION_URL}\n\n` +
      `Once payment is complete, your access will be automatically extended.\n\n` +
      `— Code Breakers NCLEX`;
    const href = `mailto:${encodeURIComponent(student.email)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = href;
  };

  // ---------------------------------------------------------------------------
  // Build the expiry payload from the picker state. Returns either
  // { daysUntilExpiry: <number> } or { expiryDate: <ISO> } or {} for no expiry.
  // ---------------------------------------------------------------------------
  const buildExpiryPayload = () => {
    if (grantDuration === 'none') return {};
    if (grantDuration === 'custom') {
      if (!grantCustomDate) return {};
      // 23:59:59 on the picked day in the admin's browser timezone, sent as
      // UTC, so access lasts the full day the admin picked.
      return { expiryDate: endOfLocalDayISO(grantCustomDate) };
    }
    return { daysUntilExpiry: Number(grantDuration) };
  };

  const formatExpiryNote = () => {
    const payload = buildExpiryPayload();
    if (payload.expiryDate) {
      return `Expires ${formatDate(payload.expiryDate)}.`;
    }
    if (payload.daysUntilExpiry) {
      const d = new Date(Date.now() + payload.daysUntilExpiry * 24 * 60 * 60 * 1000);
      return `Expires ${formatDate(d.toISOString())} (${payload.daysUntilExpiry} days).`;
    }
    return 'No expiration date - access stays active until manually revoked.';
  };

  // ---------------------------------------------------------------------------
  // Single-student grant/revoke
  // ---------------------------------------------------------------------------
  const doGrant = async (student) => {
    setBusyStudentId(student.id);
    const { error } = await authHelpers.thinkificAction('grant', {
      studentId: student.id,
      ...buildExpiryPayload(),
    });
    setBusyStudentId(null);
    if (error) {
      showToast('error', `Grant failed for ${student.email}: ${error.message}`);
    } else {
      showToast('success', `Granted NCLEX access to ${student.email}.`);
      await refreshStudents();
    }
    setConfirm(null);
  };

  const doRevoke = async (student) => {
    setBusyStudentId(student.id);
    const { error } = student._isThinkificOnly
      ? await authHelpers.thinkificAction('revoke_unlinked', {
          thinkificEnrollmentId: student.thinkific_enrollment_id,
        })
      : await authHelpers.thinkificAction('revoke', { studentId: student.id });
    setBusyStudentId(null);
    if (error) {
      showToast('error', `Revoke failed for ${student.email}: ${error.message}`);
    } else {
      showToast('success', `Revoked NCLEX access for ${student.email}.`);
      if (student._isThinkificOnly) {
        await refreshUnlinked();
      } else {
        await refreshStudents();
      }
    }
    setConfirm(null);
  };

  // ---------------------------------------------------------------------------
  // Bulk by cohort
  // ---------------------------------------------------------------------------
  const doBulk = async (cohortId, mode) => {
    setBulkBusy(true);
    setBulkResult(null);
    const expiryPayload = mode === 'grant' ? buildExpiryPayload() : {};
    const { data, error } = await authHelpers.thinkificAction(
      mode === 'grant' ? 'bulk_grant' : 'bulk_revoke',
      { cohortId, ...expiryPayload }
    );
    setBulkBusy(false);
    if (error) {
      showToast('error', `Bulk ${mode} failed: ${error.message}`);
    } else {
      setBulkResult({ ...data, mode });
      showToast(
        data.failed === 0 ? 'success' : 'error',
        `Bulk ${mode}: ${data.succeeded} succeeded, ${data.failed} failed.`
      );
      await refreshStudents();
    }
    setConfirm(null);
  };

  // ---------------------------------------------------------------------------
  // Progress Report (PDF)
  // ---------------------------------------------------------------------------
  // Fetches the Thinkific progress payload for every student in `subjects`
  // and stitches the results into a single multi-page PDF. Calls are made
  // in series with a small delay so we don't hammer Thinkific's rate limit
  // (each call also fans out to several Thinkific endpoints server-side).
  // One student failing does not abort the run - that student's page in
  // the PDF will show the error as a warning instead.
  const runReportForSubjects = async (subjects, { pdfOptions, diagnose = false } = {}) => {
    if (!subjects || subjects.length === 0) return;

    const cancelToken = { cancelled: false };
    setReportCancel(cancelToken);
    setReportBusy(subjects.length === 1 ? subjects[0].id : '__group__');
    setReportProgress({ current: 0, total: subjects.length, label: '' });

    const reports = [];
    for (let i = 0; i < subjects.length; i++) {
      if (cancelToken.cancelled) break;
      const s = subjects[i];
      setReportProgress({
        current: i,
        total: subjects.length,
        label: s.name || s.email || '',
      });

      const payload = s._isThinkificOnly
        ? { thinkificEnrollmentId: s.thinkific_enrollment_id }
        : { studentId: s.id };
      const { data, error } = await authHelpers.thinkificReport({ ...payload, diagnose });

      if (error) {
        // Synthesize a placeholder report so this student still shows in
        // the PDF with the error explained inline. Keeps group reports
        // resilient to single-student failures.
        reports.push({
          subject: {
            displayName: s.name || s.email,
            email: s.email,
            cohortName: s.cohortId ? cohortLookup.get(s.cohortId) : null,
          },
          enrollment: null,
          activity: [],
          quizzes: [],
          certificate: null,
          warnings: [error.message || 'Failed to load Thinkific data.'],
        });
      } else if (data?.report) {
        // Backfill cohortName when the server didn't have it (unlinked rows).
        if (!data.report.subject.cohortName && s.cohortId) {
          data.report.subject.cohortName = cohortLookup.get(s.cohortId) || null;
        }
        reports.push(data.report);
      }

      // Throttle - keep us comfortably under Thinkific's rate limit.
      if (i < subjects.length - 1) {
        await new Promise((r) => setTimeout(r, 500));
      }
    }

    setReportProgress({
      current: subjects.length,
      total: subjects.length,
      label: 'Rendering PDF…',
    });

    try {
      if (reports.length > 0 && !cancelToken.cancelled) {
        generateThinkificReportPdf(reports, pdfOptions || {});
        showToast(
          'success',
          subjects.length === 1
            ? `Report generated for ${subjects[0].name || subjects[0].email}.`
            : `Report generated for ${reports.length} students.`,
        );
      } else if (cancelToken.cancelled) {
        showToast('error', 'Report cancelled.');
      }
    } catch (err) {
      showToast('error', `Could not build PDF: ${err.message}`);
    } finally {
      setReportBusy(null);
      setReportProgress(null);
    }
  };

  const runSingleStudentReport = (student, { diagnose = false } = {}) =>
    runReportForSubjects([student], {
      diagnose,
      pdfOptions: {
        reportTitle: diagnose
          ? 'Thinkific Progress Report (Diagnostic)'
          : 'Thinkific Progress Report',
        generatedFor: student.cohortId
          ? cohortLookup.get(student.cohortId) || null
          : null,
      },
    });

  const runCohortReport = (cohortId) => {
    const subjects = allStudents.filter((s) => s.cohortId === cohortId);
    if (subjects.length === 0) {
      showToast('error', 'No students in this cohort to report on.');
      return;
    }
    return runReportForSubjects(subjects, {
      pdfOptions: {
        reportTitle: 'Thinkific Progress Report',
        generatedFor: cohortLookup.get(cohortId) || 'Cohort',
      },
    });
  };

  // Generate a report for whatever the admin is currently looking at -
  // useful for filtered views like "everyone with active access" or
  // "Thinkific-only students". Confirms first when the count is large.
  const runFilteredReport = () => {
    if (filtered.length === 0) {
      showToast('error', 'No students match the current filters.');
      return;
    }
    if (filtered.length > 25) {
      const ok = window.confirm(
        `This will generate a PDF report for ${filtered.length} students. ` +
        `It may take ${Math.ceil((filtered.length * 1.5) / 60)}+ minute(s). Continue?`
      );
      if (!ok) return;
    }
    runReportForSubjects(filtered, {
      pdfOptions: {
        reportTitle: 'Thinkific Progress Report',
        generatedFor:
          filterCohort !== 'all'
            ? cohortLookup.get(filterCohort) || 'Filtered students'
            : 'Filtered students',
      },
    });
  };

  // ---------------------------------------------------------------------------
  // Sync from Thinkific
  // ---------------------------------------------------------------------------
  const doSync = async () => {
    setSyncBusy(true);
    // 365-day window is the function's default - keeps the call inside
    // Netlify's 10s budget on large rosters. Webhooks keep things fresh
    // in real-time so this catch-up window doesn't need to be wider.
    // Sync covers enrollment + progress data; paid status flows through
    // the order.created webhook in real-time and doesn't need backfill
    // here.
    const { data, error } = await authHelpers.thinkificAction('sync');
    setSyncBusy(false);
    if (error) {
      showToast('error', `Sync failed: ${error.message}`);
    } else {
      const unlinkedNote = data.unlinked_upserted
        ? ` ${data.unlinked_upserted} Thinkific-only enrollment(s) tracked.`
        : '';
      const windowNote = data.window_days
        ? ` (last ${data.window_days} days)`
        : '';
      showToast(
        'success',
        `Sync complete${windowNote}: ${data.profiles_updated} profile(s) updated from ${data.enrollments_seen} Thinkific enrollment(s).${unlinkedNote}`
      );
      await Promise.all([refreshStudents(), refreshUnlinked()]);
    }
  };

  // ---------------------------------------------------------------------------
  // Add Thinkific Student (no LMS profile required)
  // ---------------------------------------------------------------------------
  const buildAddPayload = () => {
    const base = {
      email: addForm.email,
      firstName: addForm.firstName,
      lastName: addForm.lastName,
    };
    if (addForm.duration === 'none') return base;
    if (addForm.duration === 'custom') {
      if (!addForm.customDate) return base;
      base.expiryDate = new Date(`${addForm.customDate}T23:59:59`).toISOString();
      return base;
    }
    base.daysUntilExpiry = Number(addForm.duration);
    return base;
  };

  const doAddStudent = async (e) => {
    e?.preventDefault?.();
    setAddBusy(true);
    const { data, error } = await authHelpers.thinkificAction(
      'enroll_by_email',
      buildAddPayload()
    );
    setAddBusy(false);
    if (error) {
      showToast('error', `Failed to add: ${error.message}`);
      return;
    }
    const note = data.linked_to_lms_profile
      ? `${addForm.email} matched an existing LMS student and was linked there.`
      : `${addForm.email} added as a Thinkific-only student.`;
    showToast('success', `Granted NCLEX access. ${note}`);
    setShowAddModal(false);
    setAddForm({
      firstName: '',
      lastName: '',
      email: '',
      duration: '42',
      customDate: '',
    });
    await Promise.all([refreshStudents(), refreshUnlinked()]);
  };

  // ---------------------------------------------------------------------------
  // Bulk import (CSV-style paste) - mass enroll students into the NCLEX
  // Thinkific course. Accepts these formats per line:
  //   First Last,email@example.com
  //   First,Last,email@example.com
  //   "Last, First",email@example.com
  //   First Last\temail@example.com
  //   email@example.com   (name derived from the email prefix)
  // ---------------------------------------------------------------------------
  const parseBulkImport = () => {
    const text = bulkImportText || '';
    if (!text.trim()) return;
    const lines = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);

    const seen = new Set();
    const existingThinkificEmails = new Set(
      (unlinked || []).map((u) => (u.email || '').toLowerCase())
    );
    const parsed = [];

    const cleanName = (raw) => raw.replace(/^["']|["']$/g, '').trim();

    const nameFromEmail = (email) =>
      email
        .split('@')[0]
        .replace(/[._-]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .replace(/\b\w/g, (c) => c.toUpperCase());

    const splitFirstLast = (raw) => {
      const cleaned = cleanName(raw);
      if (!cleaned) return { firstName: '', lastName: '' };
      // "Last, First" style.
      if (cleaned.includes(',')) {
        const [last, first] = cleaned.split(',').map((p) => p.trim());
        return { firstName: first || '', lastName: last || '' };
      }
      const parts = cleaned.split(/\s+/);
      if (parts.length === 1) return { firstName: parts[0], lastName: '(LMS Student)' };
      return { firstName: parts[0], lastName: parts.slice(1).join(' ') };
    };

    for (const line of lines) {
      let firstName = '';
      let lastName = '';
      let email = '';
      let error = null;

      // Tab-separated wins over comma when present.
      const tokens = line.includes('\t')
        ? line.split('\t').map((t) => t.trim()).filter(Boolean)
        : line.split(',').map((t) => t.trim()).filter(Boolean);

      // Find the token that looks like an email; the rest are name parts.
      const emailIdx = tokens.findIndex((t) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(t));

      if (emailIdx === -1) {
        // No email - reject the row.
        parsed.push({
          firstName: '',
          lastName: '',
          email: tokens[0] || line,
          status: 'error',
          error: 'No email address',
        });
        continue;
      }

      email = tokens[emailIdx].toLowerCase();
      const nameTokens = tokens.filter((_, i) => i !== emailIdx);

      if (nameTokens.length === 0) {
        const guess = nameFromEmail(email);
        ({ firstName, lastName } = splitFirstLast(guess));
      } else if (nameTokens.length === 1) {
        ({ firstName, lastName } = splitFirstLast(nameTokens[0]));
      } else {
        // Two-or-more tokens before / after the email - assume "First, Last".
        firstName = cleanName(nameTokens[0]);
        lastName = cleanName(nameTokens.slice(1).join(' '));
      }

      if (!firstName) firstName = nameFromEmail(email);
      if (!lastName) lastName = '(LMS Student)';

      let status = 'ready';
      if (seen.has(email)) {
        status = 'skip';
        error = 'Duplicate row';
      } else if (existingThinkificEmails.has(email)) {
        // Not a blocker - the server upserts unlinked rows and bumps
        // expiry on the existing enrollment, but flag it so admins
        // know they're re-granting, not creating fresh.
        status = 'ready';
        error = 'Already has Thinkific access (will refresh)';
      }
      seen.add(email);
      parsed.push({ firstName, lastName, email, status, error });
    }

    setBulkImportParsed(parsed);
    setBulkImportStep('preview');
  };

  const buildBulkImportExpiry = () => {
    if (bulkImportDuration === 'none') return {};
    if (bulkImportDuration === 'custom') {
      if (!bulkImportCustomDate) return {};
      return {
        expiryDate: new Date(`${bulkImportCustomDate}T23:59:59`).toISOString(),
      };
    }
    return { daysUntilExpiry: Number(bulkImportDuration) };
  };

  const runBulkImport = async () => {
    const ready = bulkImportParsed.filter((p) => p.status === 'ready');
    if (ready.length === 0) return;
    if (ready.length > BULK_IMPORT_MAX) {
      showToast(
        'error',
        `Bulk import is limited to ${BULK_IMPORT_MAX} students per request. Split your list and try again.`
      );
      return;
    }

    setBulkImportBusy(true);
    setBulkImportStep('importing');

    const { data, error } = await authHelpers.thinkificAction('bulk_enroll_by_email', {
      students: ready.map((r) => ({
        firstName: r.firstName,
        lastName: r.lastName,
        email: r.email,
      })),
      ...buildBulkImportExpiry(),
    });

    setBulkImportBusy(false);

    if (error) {
      showToast('error', `Bulk import failed: ${error.message}`);
      setBulkImportStep('preview');
      return;
    }

    // Merge per-row results back onto the preview rows so the admin can
    // see exactly which lines went through.
    const resultsByEmail = new Map((data.results || []).map((r) => [r.email, r]));
    const merged = bulkImportParsed.map((p) => {
      if (p.status !== 'ready') return p;
      const r = resultsByEmail.get(p.email);
      if (!r) return { ...p, status: 'error', error: 'No server response' };
      if (r.ok) return { ...p, status: 'success', error: null };
      return { ...p, status: 'error', error: r.error || 'Failed' };
    });

    setBulkImportResults(merged);
    setBulkImportStep('done');
    showToast(
      data.failed === 0 ? 'success' : 'error',
      `Bulk import: ${data.succeeded} succeeded, ${data.failed} failed (of ${data.total}).`
    );
    await Promise.all([refreshStudents(), refreshUnlinked()]);
  };

  const resetBulkImport = () => {
    setBulkImportStep('input');
    setBulkImportText('');
    setBulkImportParsed([]);
    setBulkImportResults([]);
    setBulkImportDuration('42');
    setBulkImportCustomDate('');
  };

  const closeBulkImport = () => {
    if (bulkImportBusy) return;
    setShowBulkImport(false);
    resetBulkImport();
  };

  // ---------------------------------------------------------------------------
  // Historical paid backfill (Netlify Background Function)
  // ---------------------------------------------------------------------------
  const doBackfill = async () => {
    setBackfillBusy(true);
    setConfirmBackfill(false);
    const { error } = await authHelpers.thinkificPaidBackfill();
    if (error) {
      showToast('error', `Backfill failed to start: ${error.message}`);
      setBackfillBusy(false);
      return;
    }
    showToast(
      'success',
      'Backfill started in the background. This can take several minutes - refresh the page later to see updated Paid counts. Progress lands in the Audit Log.'
    );
    // Background function runs server-side; we don't have a real-time
    // completion signal. Clear the busy flag after a short delay so the
    // button is usable again, with a longer-lived toast informing the
    // admin to refresh.
    setTimeout(() => setBackfillBusy(false), 10000);
  };

  // ---------------------------------------------------------------------------
  // Audit log
  // ---------------------------------------------------------------------------
  const openAuditLog = async () => {
    setShowAuditLog(true);
    setAuditLoading(true);
    const { data, error } = await db.getThinkificAuditLog({ limit: 200 });
    setAuditLoading(false);
    if (error) {
      showToast('error', `Failed to load audit log: ${error.message}`);
      return;
    }
    setAuditEntries(data || []);
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  const formatTime = (ts) => {
    if (!ts) return '—';
    const d = new Date(ts);
    return d.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const formatDate = (ts) => {
    if (!ts) return '—';
    const d = new Date(ts);
    return d.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  // Thinkific can return percentage_completed as either 0-1 (fraction) or
  // 0-100 (percent). Normalize so we always render a percent.
  const percentValue = (v) => {
    if (v == null) return null;
    const n = Number(v);
    if (!Number.isFinite(n)) return null;
    return n > 1 ? n : n * 100;
  };

  const formatPercent = (v) => {
    const n = percentValue(v);
    if (n == null) return '—';
    return `${Math.round(n)}%`;
  };

  // Relative-time string for "Last Sign-in" column - friendlier than a
  // raw datetime when the data is recent. Falls back to a date for
  // anything older than a week.
  const formatRelative = (ts) => {
    if (!ts) return '—';
    const then = new Date(ts).getTime();
    if (!Number.isFinite(then)) return '—';
    const diffSec = Math.max(0, Math.floor((Date.now() - then) / 1000));
    if (diffSec < 60) return 'just now';
    if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
    if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
    if (diffSec < 7 * 86400) return `${Math.floor(diffSec / 86400)}d ago`;
    return formatDate(ts);
  };

  // Days remaining until enrollment expiry. Returns null when there is
  // no expiry set, negative when already expired.
  const daysRemaining = (expiryDate) => {
    if (!expiryDate) return null;
    const t = new Date(expiryDate).getTime();
    if (!Number.isFinite(t)) return null;
    return Math.ceil((t - Date.now()) / (24 * 60 * 60 * 1000));
  };

  const statusBadgeStyle = (key) => {
    const base = {
      display: 'inline-block',
      padding: '2px 10px',
      borderRadius: '999px',
      fontSize: '12px',
      fontWeight: 600,
      lineHeight: '20px',
    };
    if (key === 'active') return { ...base, background: '#d1fae5', color: '#065f46' };
    if (key === 'expired') return { ...base, background: '#fee2e2', color: '#991b1b' };
    if (key === 'never') return { ...base, background: '#fef3c7', color: '#92400e' };
    return { ...base, background: '#e5e7eb', color: '#4b5563' };
  };

  // Defensive role check - the route in App.jsx is admin-only but we re-check
  // here so the page is safe even if the route gate is ever loosened.
  // Placed after all hooks so the Rules of Hooks aren't violated.
  if (!isAdmin) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>
        This page is only available to administrators.
      </div>
    );
  }

  return (
    <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
      {/* Header */}
      <div className="page-header" style={{ marginBottom: '16px' }}>
        <div>
          <h1>🎓 NCLEX Review Course Access</h1>
          <p>
            Grant or revoke student access to the Thinkific NCLEX Review Course.
            New students receive an automatic welcome email from Thinkific with their login.
          </p>
        </div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <button
            className="btn-primary"
            onClick={() => setShowAddModal(true)}
            title="Create a Thinkific account and grant NCLEX course access without adding the student to an LMS cohort"
          >
            ➕ Add Thinkific Student
          </button>
          <button
            className="btn-secondary"
            onClick={() => setShowBulkImport(true)}
            title="Paste a CSV-style list of students to grant NCLEX course access to all of them at once"
          >
            📋 Bulk Import
          </button>
          <button
            className="btn-secondary"
            onClick={runFilteredReport}
            disabled={!!reportBusy || filtered.length === 0}
            title="Generate a PDF progress report for every student currently visible in the list (apply filters to narrow the scope)."
          >
            📊 Run Report ({filtered.length})
          </button>
          <button className="btn-secondary" onClick={openAuditLog}>
            📜 Audit Log
          </button>
          <button
            className="btn-secondary"
            onClick={() => setConfirmBackfill(true)}
            disabled={backfillBusy}
            title="One-time job to mark every historical NCLEX buyer as Paid. Runs in the background."
          >
            {backfillBusy ? 'Backfill running…' : '💳 Backfill Paid Status'}
          </button>
          <button className="btn-secondary" onClick={doSync} disabled={syncBusy}>
            {syncBusy ? 'Syncing…' : '🔄 Sync from Thinkific'}
          </button>
        </div>
      </div>

      {/* Stats */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
          gap: '12px',
          marginBottom: '20px',
        }}
      >
        {[
          { label: 'Has Access', value: counts.active, key: 'active' },
          { label: 'Access Expired', value: counts.expired, key: 'expired' },
          { label: 'No Access', value: counts.never, key: 'never' },
          { label: 'Not Linked', value: counts.unlinked, key: 'unlinked' },
          { label: 'Thinkific only', value: counts.thinkific_only, key: 'thinkific_only' },
          { label: '💳 Paid', value: counts.paid, key: 'paid' },
          { label: '🎁 Free / Comped', value: counts.free, key: 'free' },
        ].map((stat) => (
          <div
            key={stat.key}
            style={{
              background: 'var(--card-bg, #fff)',
              borderRadius: '12px',
              padding: '14px 16px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
            }}
          >
            <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '4px' }}>
              {stat.label}
            </div>
            <div style={{ fontSize: '24px', fontWeight: 700 }}>{stat.value}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div
        style={{
          background: 'var(--card-bg, #fff)',
          borderRadius: '12px',
          padding: '16px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
          marginBottom: '16px',
          display: 'grid',
          gridTemplateColumns: '2fr 1fr 1fr 1fr auto',
          gap: '12px',
          alignItems: 'end',
        }}
      >
        <div className="form-group" style={{ margin: 0 }}>
          <label>Search</label>
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Name or email…"
          />
        </div>
        <div className="form-group" style={{ margin: 0 }}>
          <label>Cohort</label>
          <select value={filterCohort} onChange={(e) => setFilterCohort(e.target.value)}>
            <option value="all">All cohorts (incl. Thinkific-only)</option>
            <option value="unassigned">Unassigned (LMS, no cohort)</option>
            <option value="thinkific-only">Thinkific only (not in LMS)</option>
            {(cohorts || []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>
        <div className="form-group" style={{ margin: 0 }}>
          <label>Access status</label>
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
            <option value="all">All statuses</option>
            <option value="active">Has Access</option>
            <option value="expired">Access Expired</option>
            <option value="never">No Access</option>
            <option value="unlinked">Not Linked</option>
          </select>
        </div>
        <div className="form-group" style={{ margin: 0 }}>
          <label>Payment</label>
          <select value={filterPayment} onChange={(e) => setFilterPayment(e.target.value)}>
            <option value="all">All</option>
            <option value="paid">💳 Paid only</option>
            <option value="free">🎁 Free / Comped</option>
          </select>
        </div>
        <button
          className="btn-secondary"
          onClick={() => {
            setSearchTerm('');
            setFilterCohort('all');
            setFilterStatus('all');
            setFilterPayment('all');
          }}
        >
          Reset
        </button>
      </div>

      {/* Bulk-by-cohort */}
      {filterCohort !== 'all' && filterCohort !== 'unassigned' && (
        <div
          style={{
            background: '#eff6ff',
            border: '1px solid #bfdbfe',
            borderRadius: '12px',
            padding: '14px 16px',
            marginBottom: '16px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '10px',
          }}
        >
          <div style={{ color: '#1e3a8a' }}>
            <strong>Bulk actions for {cohortLookup.get(filterCohort) || 'this cohort'}:</strong>{' '}
            apply to every active student in the cohort.
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              className="btn-primary"
              disabled={bulkBusy}
              onClick={() =>
                setConfirm({ type: 'bulk_grant', cohortId: filterCohort })
              }
            >
              {bulkBusy ? 'Working…' : 'Grant access to entire cohort'}
            </button>
            <button
              className="btn-secondary"
              disabled={bulkBusy}
              onClick={() =>
                setConfirm({ type: 'bulk_revoke', cohortId: filterCohort })
              }
            >
              Revoke access for entire cohort
            </button>
            <button
              className="btn-secondary"
              disabled={!!reportBusy}
              onClick={() => runCohortReport(filterCohort)}
              title="Generate a PDF progress report for every student in this cohort"
            >
              📊 Progress Report (PDF)
            </button>
          </div>
        </div>
      )}

      {/* Bulk result */}
      {bulkResult && (
        <div
          style={{
            background: bulkResult.failed === 0 ? '#ecfdf5' : '#fef2f2',
            border: `1px solid ${bulkResult.failed === 0 ? '#a7f3d0' : '#fecaca'}`,
            borderRadius: '12px',
            padding: '12px 16px',
            marginBottom: '16px',
          }}
        >
          <strong>Bulk {bulkResult.mode} complete:</strong> {bulkResult.succeeded} succeeded,{' '}
          {bulkResult.failed} failed (of {bulkResult.total}).
          {bulkResult.failed > 0 && (
            <details style={{ marginTop: '8px' }}>
              <summary style={{ cursor: 'pointer' }}>Show failures</summary>
              <ul style={{ margin: '8px 0 0', paddingLeft: '20px' }}>
                {bulkResult.results
                  .filter((r) => !r.ok)
                  .map((r) => (
                    <li key={r.student_id}>
                      {r.email}: {r.error}
                    </li>
                  ))}
              </ul>
            </details>
          )}
          <button
            className="btn-secondary"
            style={{ marginTop: '10px' }}
            onClick={() => setBulkResult(null)}
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Student list */}
      <div
        style={{
          background: 'var(--card-bg, #fff)',
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            padding: '12px 16px',
            borderBottom: '1px solid #e5e7eb',
            fontSize: '13px',
            color: '#6b7280',
          }}
        >
          Showing {filtered.length} of {allStudents.length} student
          {allStudents.length === 1 ? '' : 's'}
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead style={{ background: '#f9fafb' }}>
              <tr>
                <th style={thStyle}>Student</th>
                <th style={thStyle}>Email</th>
                <th style={thStyle}>Cohort</th>
                <th style={thStyle}>Access</th>
                <th style={thStyle}>Progress</th>
                <th style={thStyle}>Last Sign-in</th>
                <th style={thStyle}>Days Remaining</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={8} style={{ padding: '24px', textAlign: 'center', color: '#9ca3af' }}>
                    No students match the current filters.
                  </td>
                </tr>
              )}
              {filtered.map((s) => {
                const isActive = effectiveStatus(s) === 'active';
                const busy = busyStudentId === s.id;
                const pct = percentValue(s.thinkific_percentage_completed);
                return (
                  <tr key={s.id} style={{ borderTop: '1px solid #f3f4f6' }}>
                    <td style={tdStyle}>
                      <button
                        type="button"
                        onClick={() => setDetailsStudent(s)}
                        style={{
                          background: 'none',
                          border: 'none',
                          padding: 0,
                          color: '#1d4ed8',
                          cursor: 'pointer',
                          font: 'inherit',
                          textAlign: 'left',
                        }}
                        title="View Thinkific details"
                      >
                        {s.name || '—'}
                      </button>
                    </td>
                    <td style={{ ...tdStyle, color: '#4b5563' }}>{s.email}</td>
                    <td style={tdStyle}>
                      {s._isThinkificOnly ? (
                        <span
                          style={{
                            display: 'inline-block',
                            padding: '2px 8px',
                            borderRadius: '999px',
                            background: '#fef3c7',
                            color: '#92400e',
                            fontSize: '12px',
                            fontWeight: 600,
                          }}
                          title="Exists in Thinkific but not in the LMS"
                        >
                          Not in LMS
                        </span>
                      ) : s.cohortId ? (
                        cohortLookup.get(s.cohortId) || '—'
                      ) : (
                        <span style={{ color: '#9ca3af' }}>Unassigned</span>
                      )}
                    </td>
                    <td style={tdStyle}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'flex-start' }}>
                        <span style={statusBadgeStyle(statusKey(s))}>
                          {statusLabel(s)}
                        </span>
                        <span style={s.thinkific_paid ? paidBadgeStyle : freeBadgeStyle}>
                          {s.thinkific_paid ? '💳 Paid' : '🎁 Free'}
                        </span>
                      </div>
                    </td>
                    <td style={tdStyle}>
                      {pct == null ? (
                        <span style={{ color: '#9ca3af' }}>—</span>
                      ) : (
                        <div style={{ minWidth: '120px' }}>
                          <div
                            style={{
                              height: '6px',
                              background: '#e5e7eb',
                              borderRadius: '999px',
                              overflow: 'hidden',
                              marginBottom: '4px',
                            }}
                          >
                            <div
                              style={{
                                width: `${Math.min(100, Math.max(0, pct))}%`,
                                height: '100%',
                                background: s.thinkific_completed ? '#059669' : '#3b82f6',
                              }}
                            />
                          </div>
                          <div style={{ fontSize: '12px', color: '#4b5563' }}>
                            {Math.round(pct)}%
                            {s.thinkific_completed && (
                              <span style={{ color: '#059669', marginLeft: '6px' }}>
                                🎓 Completed
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                    </td>
                    <td style={{ ...tdStyle, color: '#4b5563', fontSize: '13px' }}>
                      {s.thinkific_last_signin_at ? (
                        <span title={new Date(s.thinkific_last_signin_at).toLocaleString()}>
                          {formatRelative(s.thinkific_last_signin_at)}
                        </span>
                      ) : (
                        <span style={{ color: '#9ca3af' }}>—</span>
                      )}
                    </td>
                    <td style={{ ...tdStyle, fontSize: '13px' }}>
                      {(() => {
                        const days = daysRemaining(s.thinkific_expiry_date);
                        if (days == null) {
                          return <span style={{ color: '#9ca3af' }}>—</span>;
                        }
                        let color = '#4b5563';
                        if (days <= 0) color = '#991b1b';
                        else if (days <= 7) color = '#b45309';
                        return (
                          <span
                            style={{ color, fontWeight: days <= 7 ? 600 : 400 }}
                            title={`Access expires ${formatDate(s.thinkific_expiry_date)}`}
                          >
                            {days <= 0
                              ? `Expired ${Math.abs(days)}d ago`
                              : `${days}d`}
                          </span>
                        );
                      })()}
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}>
                      <div style={{
                        display: 'flex',
                        gap: '8px',
                        justifyContent: 'flex-end',
                        flexWrap: 'wrap',
                      }}>
                        <button
                          type="button"
                          onClick={() => emailExtensionLink(s)}
                          style={stripeButtonStyle}
                          title="Email this student a link to extend their NCLEX course access by 45 days"
                        >
                          💳 Send Payment Link
                        </button>
                        <button
                          type="button"
                          className="btn-secondary"
                          disabled={!!reportBusy}
                          onClick={() => runSingleStudentReport(s)}
                          title="Generate a PDF progress report (enrollment, activity feed, quizzes) for this student"
                        >
                          {reportBusy === s.id ? 'Working…' : '📊 Report'}
                        </button>
                        {isActive ? (
                          <button
                            className="btn-secondary"
                            disabled={busy}
                            onClick={() => setConfirm({ type: 'revoke', student: s })}
                          >
                            {busy ? 'Working…' : 'Revoke'}
                          </button>
                        ) : s._isThinkificOnly ? (
                          <span style={{ color: '#9ca3af', fontSize: '13px', alignSelf: 'center' }}>
                            Add to LMS to re-grant
                          </span>
                        ) : (
                          <button
                            className="btn-primary"
                            disabled={busy}
                            onClick={() => setConfirm({ type: 'grant', student: s })}
                          >
                            {busy ? 'Working…' : 'Grant Access'}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Progress Report overlay */}
      {reportProgress && (
        <div className="modal-overlay" style={{ zIndex: 10001 }}>
          <div className="modal" style={{ maxWidth: '440px' }}>
            <div className="modal-header" style={{ borderBottom: 'none' }}>
              <h3 style={{ margin: 0 }}>Generating progress report…</h3>
            </div>
            <div style={{ padding: '8px 24px 20px' }}>
              <div style={{ color: '#4b5563', marginBottom: '12px' }}>
                {reportProgress.label
                  ? <>Fetching <strong>{reportProgress.label}</strong>…</>
                  : <>Fetching Thinkific data…</>}
              </div>
              <div
                style={{
                  height: '8px',
                  background: '#e5e7eb',
                  borderRadius: '999px',
                  overflow: 'hidden',
                  marginBottom: '8px',
                }}
              >
                <div
                  style={{
                    height: '100%',
                    width: `${reportProgress.total
                      ? Math.round((reportProgress.current / reportProgress.total) * 100)
                      : 0}%`,
                    background: '#3b82f6',
                    transition: 'width 200ms',
                  }}
                />
              </div>
              <div style={{ fontSize: '13px', color: '#6b7280', textAlign: 'right' }}>
                {reportProgress.current} of {reportProgress.total}
              </div>
            </div>
            <div className="modal-footer" style={{ justifyContent: 'flex-end', borderTop: 'none' }}>
              <button
                className="btn-secondary"
                onClick={() => {
                  reportCancel.cancelled = true;
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div
          style={{
            position: 'fixed',
            bottom: '24px',
            right: '24px',
            padding: '12px 16px',
            borderRadius: '8px',
            color: '#fff',
            background: toast.kind === 'success' ? '#059669' : '#dc2626',
            boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
            maxWidth: '420px',
            zIndex: 10000,
          }}
        >
          {toast.message}
        </div>
      )}

      {/* Confirm modal */}
      {confirm && (
        <div className="modal-overlay" onClick={() => !bulkBusy && !busyStudentId && setConfirm(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>
                {confirm.type === 'grant' && 'Grant NCLEX Course Access'}
                {confirm.type === 'revoke' && 'Revoke NCLEX Course Access'}
                {confirm.type === 'bulk_grant' && 'Grant Access to Entire Cohort'}
                {confirm.type === 'bulk_revoke' && 'Revoke Access for Entire Cohort'}
              </h3>
              <button
                className="modal-close"
                onClick={() => setConfirm(null)}
                disabled={bulkBusy || busyStudentId}
              >
                ×
              </button>
            </div>
            <div style={{ padding: '16px 24px' }}>
              {confirm.type === 'grant' && (
                <p>
                  Grant <strong>{confirm.student.name}</strong> ({confirm.student.email}) access
                  to the Thinkific NCLEX Review Course?
                  <br />
                  <br />
                  If they don't already have a Thinkific account, one will be created and a
                  welcome email will be sent automatically.
                </p>
              )}
              {confirm.type === 'revoke' && (
                <p>
                  Revoke access for <strong>{confirm.student.name}</strong> (
                  {confirm.student.email})?
                  <br />
                  <br />
                  Their progress in Thinkific will be preserved, but they will no longer be able
                  to take the course.
                </p>
              )}
              {confirm.type === 'bulk_grant' && (
                <p>
                  Grant NCLEX course access to every active student in{' '}
                  <strong>{cohortLookup.get(confirm.cohortId)}</strong>?
                  <br />
                  <br />
                  Students who already have access will be skipped automatically. New Thinkific
                  accounts will receive welcome emails.
                </p>
              )}
              {confirm.type === 'bulk_revoke' && (
                <p>
                  Revoke NCLEX course access for every active student in{' '}
                  <strong>{cohortLookup.get(confirm.cohortId)}</strong>?
                  <br />
                  <br />
                  Their progress in Thinkific will be preserved.
                </p>
              )}

              {/* Expiration picker - only shown for grant flows */}
              {(confirm.type === 'grant' || confirm.type === 'bulk_grant') && (
                <div
                  style={{
                    marginTop: '8px',
                    padding: '12px',
                    background: '#f9fafb',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                  }}
                >
                  <label
                    style={{
                      display: 'block',
                      fontSize: '13px',
                      fontWeight: 600,
                      color: '#374151',
                      marginBottom: '6px',
                    }}
                  >
                    Access duration
                  </label>
                  <select
                    value={grantDuration}
                    onChange={(e) => setGrantDuration(e.target.value)}
                    style={{ width: '100%' }}
                  >
                    <option value="42">6 weeks (default)</option>
                    <option value="30">30 days</option>
                    <option value="45">45 days (matches Stripe extension)</option>
                    <option value="60">60 days</option>
                    <option value="90">90 days</option>
                    <option value="custom">Custom date…</option>
                    <option value="none">No expiration</option>
                  </select>
                  {grantDuration === 'custom' && (
                    <input
                      type="date"
                      value={grantCustomDate}
                      onChange={(e) => setGrantCustomDate(e.target.value)}
                      min={new Date().toISOString().slice(0, 10)}
                      style={{ width: '100%', marginTop: '8px' }}
                    />
                  )}
                  <p style={{ fontSize: '12px', color: '#6b7280', margin: '8px 0 0' }}>
                    {formatExpiryNote()}
                  </p>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setConfirm(null)}
                disabled={bulkBusy || busyStudentId}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-primary"
                disabled={
                  bulkBusy ||
                  busyStudentId ||
                  ((confirm.type === 'grant' || confirm.type === 'bulk_grant') &&
                    grantDuration === 'custom' &&
                    !grantCustomDate)
                }
                onClick={() => {
                  if (confirm.type === 'grant') return doGrant(confirm.student);
                  if (confirm.type === 'revoke') return doRevoke(confirm.student);
                  if (confirm.type === 'bulk_grant') return doBulk(confirm.cohortId, 'grant');
                  if (confirm.type === 'bulk_revoke') return doBulk(confirm.cohortId, 'revoke');
                }}
              >
                {bulkBusy || busyStudentId ? 'Working…' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Per-student Details modal */}
      {detailsStudent && (
        <div className="modal-overlay" onClick={() => setDetailsStudent(null)}>
          <div
            className="modal"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: '560px', width: '90vw' }}
          >
            <div className="modal-header">
              <h3>Thinkific NCLEX Course — {detailsStudent.name || detailsStudent.email}</h3>
              <button className="modal-close" onClick={() => setDetailsStudent(null)}>
                ×
              </button>
            </div>
            <div style={{ padding: '4px 24px 16px' }}>
              <div style={{ marginBottom: '12px' }}>
                <span style={statusBadgeStyle(statusKey(detailsStudent))}>
                  {statusLabel(detailsStudent)}
                </span>
                {detailsStudent.thinkific_completed && (
                  <span
                    style={{
                      ...statusBadgeStyle('active'),
                      marginLeft: '8px',
                    }}
                  >
                    🎓 Completed
                  </span>
                )}
              </div>

              {/* Progress bar */}
              {percentValue(detailsStudent.thinkific_percentage_completed) != null && (
                <div style={{ marginBottom: '16px' }}>
                  <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '6px' }}>
                    Course progress
                  </div>
                  <div
                    style={{
                      height: '10px',
                      background: '#e5e7eb',
                      borderRadius: '999px',
                      overflow: 'hidden',
                    }}
                  >
                    <div
                      style={{
                        width: `${Math.min(
                          100,
                          Math.max(0, percentValue(detailsStudent.thinkific_percentage_completed))
                        )}%`,
                        height: '100%',
                        background: detailsStudent.thinkific_completed ? '#059669' : '#3b82f6',
                      }}
                    />
                  </div>
                  <div style={{ fontSize: '13px', color: '#374151', marginTop: '4px' }}>
                    {formatPercent(detailsStudent.thinkific_percentage_completed)} complete
                  </div>
                </div>
              )}

              {/* Details list */}
              <dl
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'auto 1fr',
                  rowGap: '8px',
                  columnGap: '16px',
                  margin: 0,
                  fontSize: '14px',
                }}
              >
                <dt style={detailLabelStyle}>Email</dt>
                <dd style={detailValueStyle}>{detailsStudent.email}</dd>

                <dt style={detailLabelStyle}>Cohort</dt>
                <dd style={detailValueStyle}>
                  {detailsStudent._isThinkificOnly
                    ? 'Not in LMS'
                    : detailsStudent.cohortId
                    ? cohortLookup.get(detailsStudent.cohortId) || '—'
                    : 'Unassigned'}
                </dd>

                <dt style={detailLabelStyle}>Started course</dt>
                <dd style={detailValueStyle}>{formatDate(detailsStudent.thinkific_started_at)}</dd>

                <dt style={detailLabelStyle}>Access activated</dt>
                <dd style={detailValueStyle}>{formatDate(detailsStudent.thinkific_activated_at)}</dd>

                <dt style={detailLabelStyle}>Completed course</dt>
                <dd style={detailValueStyle}>{formatDate(detailsStudent.thinkific_completed_at)}</dd>

                <dt style={detailLabelStyle}>Access expires</dt>
                <dd style={detailValueStyle}>{formatDate(detailsStudent.thinkific_expiry_date)}</dd>

                <dt style={detailLabelStyle}>Last sign-in</dt>
                <dd style={detailValueStyle}>{formatTime(detailsStudent.thinkific_last_signin_at)}</dd>

                <dt style={detailLabelStyle}>Payment</dt>
                <dd style={detailValueStyle}>
                  <span style={detailsStudent.thinkific_paid ? paidBadgeStyle : freeBadgeStyle}>
                    {detailsStudent.thinkific_paid ? '💳 Paid' : '🎁 Free / Comped'}
                  </span>
                  {detailsStudent.thinkific_paid && detailsStudent.thinkific_first_paid_at && (
                    <span style={{ fontSize: '13px', color: '#6b7280', marginLeft: '8px' }}>
                      since {formatDate(detailsStudent.thinkific_first_paid_at)}
                    </span>
                  )}
                </dd>

                <dt style={detailLabelStyle}>Last synced</dt>
                <dd style={detailValueStyle}>{formatTime(detailsStudent.thinkific_synced_at)}</dd>

                <dt style={detailLabelStyle}>Thinkific user ID</dt>
                <dd style={{ ...detailValueStyle, fontFamily: 'monospace', fontSize: '13px' }}>
                  {detailsStudent.thinkific_user_id ?? '—'}
                </dd>

                <dt style={detailLabelStyle}>Thinkific enrollment ID</dt>
                <dd style={{ ...detailValueStyle, fontFamily: 'monospace', fontSize: '13px' }}>
                  {detailsStudent.thinkific_enrollment_id ?? '—'}
                </dd>
              </dl>

              <p style={{ fontSize: '12px', color: '#9ca3af', marginTop: '14px', marginBottom: 0 }}>
                Stats reflect the last sync from Thinkific. Click "🔄 Sync from Thinkific" on the
                main page to refresh.
              </p>
            </div>
            <div className="modal-footer">
              {effectiveStatus(detailsStudent) === 'active' ? (
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => {
                    const s = detailsStudent;
                    setDetailsStudent(null);
                    setConfirm({ type: 'revoke', student: s });
                  }}
                >
                  Revoke Access
                </button>
              ) : detailsStudent._isThinkificOnly ? (
                // Granting requires an LMS profile - point the admin
                // there instead of trying to do it from this modal.
                <span style={{ fontSize: '13px', color: '#6b7280', alignSelf: 'center' }}>
                  Add this student to the LMS in Manage Users to re-grant access.
                </span>
              ) : (
                <button
                  type="button"
                  className="btn-primary"
                  onClick={() => {
                    const s = detailsStudent;
                    setDetailsStudent(null);
                    setConfirm({ type: 'grant', student: s });
                  }}
                >
                  Grant Access
                </button>
              )}
              <button
                type="button"
                className="btn-secondary"
                disabled={!!reportBusy}
                onClick={() => {
                  const s = detailsStudent;
                  setDetailsStudent(null);
                  runSingleStudentReport(s);
                }}
                title="Generate a PDF progress report for this student"
              >
                📊 Progress Report (PDF)
              </button>
              <button
                type="button"
                className="btn-secondary"
                disabled={!!reportBusy}
                onClick={() => {
                  const s = detailsStudent;
                  setDetailsStudent(null);
                  runSingleStudentReport(s, { diagnose: true });
                }}
                title="One-off diagnostic: runs several candidate Thinkific endpoints and appends the raw responses to the PDF so we can identify which one returns lesson/quiz data on this plan."
              >
                🔬 Diagnostic
              </button>
              <button className="btn-secondary" onClick={() => setDetailsStudent(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Thinkific Student modal */}
      {showAddModal && (
        <div
          className="modal-overlay"
          onClick={() => !addBusy && setShowAddModal(false)}
        >
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>➕ Add Thinkific Student</h3>
              <button
                className="modal-close"
                onClick={() => setShowAddModal(false)}
                disabled={addBusy}
              >
                ×
              </button>
            </div>
            <form onSubmit={doAddStudent}>
              <div style={{ padding: '8px 24px 16px' }}>
                <p style={{ marginTop: 0, color: '#4b5563', fontSize: '14px' }}>
                  Creates a new Thinkific account and grants access to the NCLEX
                  Review Course. The student will get an automatic welcome email
                  from Thinkific with their login. <strong>No LMS account is
                  created.</strong>
                </p>
                <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '16px' }}>
                  If the email matches an existing LMS student, they'll be linked to
                  that student's profile automatically.
                </p>
                <div className="form-group">
                  <label>First name</label>
                  <input
                    type="text"
                    value={addForm.firstName}
                    onChange={(e) => setAddForm({ ...addForm, firstName: e.target.value })}
                    required
                    disabled={addBusy}
                  />
                </div>
                <div className="form-group">
                  <label>Last name</label>
                  <input
                    type="text"
                    value={addForm.lastName}
                    onChange={(e) => setAddForm({ ...addForm, lastName: e.target.value })}
                    required
                    disabled={addBusy}
                  />
                </div>
                <div className="form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    value={addForm.email}
                    onChange={(e) => setAddForm({ ...addForm, email: e.target.value })}
                    required
                    disabled={addBusy}
                    placeholder="student@example.com"
                  />
                </div>
                <div className="form-group">
                  <label>Access duration</label>
                  <select
                    value={addForm.duration}
                    onChange={(e) => setAddForm({ ...addForm, duration: e.target.value })}
                    disabled={addBusy}
                  >
                    <option value="42">6 weeks (default)</option>
                    <option value="30">30 days</option>
                    <option value="45">45 days (matches Stripe extension)</option>
                    <option value="60">60 days</option>
                    <option value="90">90 days</option>
                    <option value="custom">Custom date…</option>
                    <option value="none">No expiration</option>
                  </select>
                  {addForm.duration === 'custom' && (
                    <input
                      type="date"
                      value={addForm.customDate}
                      onChange={(e) => setAddForm({ ...addForm, customDate: e.target.value })}
                      min={new Date().toISOString().slice(0, 10)}
                      style={{ width: '100%', marginTop: '8px' }}
                      disabled={addBusy}
                    />
                  )}
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setShowAddModal(false)}
                  disabled={addBusy}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={
                    addBusy ||
                    !addForm.firstName.trim() ||
                    !addForm.lastName.trim() ||
                    !addForm.email.trim() ||
                    (addForm.duration === 'custom' && !addForm.customDate)
                  }
                >
                  {addBusy ? 'Adding…' : 'Add & Grant Access'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Bulk Import modal */}
      {showBulkImport && (
        <div className="modal-overlay" onClick={closeBulkImport}>
          <div
            className="modal"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: '720px', width: '92vw' }}
          >
            <div className="modal-header">
              <h3>
                {bulkImportStep === 'input' && '📋 Bulk Import NCLEX Students'}
                {bulkImportStep === 'preview' && '📋 Bulk Import — Preview'}
                {bulkImportStep === 'importing' && '📋 Bulk Import — Importing…'}
                {bulkImportStep === 'done' && '📋 Bulk Import — Results'}
              </h3>
              <button
                className="modal-close"
                onClick={closeBulkImport}
                disabled={bulkImportBusy}
              >
                ×
              </button>
            </div>

            {bulkImportStep === 'input' && (
              <>
                <div style={{ padding: '12px 24px 8px' }}>
                  <p style={{ marginTop: 0, color: '#4b5563', fontSize: '14px' }}>
                    Paste up to <strong>{BULK_IMPORT_MAX}</strong> students. One per line.
                    A Thinkific account will be created for each (welcome emails sent automatically)
                    and they'll be granted access to the NCLEX Review Course.
                  </p>
                  <details style={{ marginBottom: '10px', fontSize: '13px', color: '#4b5563' }}>
                    <summary style={{ cursor: 'pointer', fontWeight: 600 }}>
                      Supported formats
                    </summary>
                    <ul style={{ margin: '8px 0 0', paddingLeft: '20px' }}>
                      <li><code>First Last,email@example.com</code></li>
                      <li><code>First,Last,email@example.com</code></li>
                      <li><code>"Last, First",email@example.com</code></li>
                      <li><code>First Last&nbsp;[TAB]&nbsp;email@example.com</code></li>
                      <li><code>email@example.com</code> (name derived from the email)</li>
                    </ul>
                  </details>
                  <div className="form-group">
                    <label>Students</label>
                    <textarea
                      value={bulkImportText}
                      onChange={(e) => setBulkImportText(e.target.value)}
                      placeholder={`Jane Doe,jane@example.com\nJohn,Smith,john@example.com\nstudent@example.com`}
                      rows={10}
                      style={{
                        width: '100%',
                        fontFamily: 'monospace',
                        fontSize: '13px',
                      }}
                    />
                  </div>
                  <div className="form-group">
                    <label>Access duration (applies to every student)</label>
                    <select
                      value={bulkImportDuration}
                      onChange={(e) => setBulkImportDuration(e.target.value)}
                    >
                      <option value="42">6 weeks (default)</option>
                      <option value="30">30 days</option>
                      <option value="45">45 days (matches Stripe extension)</option>
                      <option value="60">60 days</option>
                      <option value="90">90 days</option>
                      <option value="custom">Custom date…</option>
                      <option value="none">No expiration</option>
                    </select>
                    {bulkImportDuration === 'custom' && (
                      <input
                        type="date"
                        value={bulkImportCustomDate}
                        onChange={(e) => setBulkImportCustomDate(e.target.value)}
                        min={new Date().toISOString().slice(0, 10)}
                        style={{ width: '100%', marginTop: '8px' }}
                      />
                    )}
                  </div>
                </div>
                <div className="modal-footer">
                  <button className="btn-secondary" onClick={closeBulkImport}>
                    Cancel
                  </button>
                  <button
                    className="btn-primary"
                    onClick={parseBulkImport}
                    disabled={
                      !bulkImportText.trim() ||
                      (bulkImportDuration === 'custom' && !bulkImportCustomDate)
                    }
                  >
                    Preview →
                  </button>
                </div>
              </>
            )}

            {bulkImportStep === 'preview' && (
              <>
                <div style={{ padding: '8px 24px 4px' }}>
                  {(() => {
                    const readyCount = bulkImportParsed.filter((p) => p.status === 'ready').length;
                    const errorCount = bulkImportParsed.filter((p) => p.status === 'error').length;
                    const skipCount = bulkImportParsed.filter((p) => p.status === 'skip').length;
                    return (
                      <div
                        style={{
                          display: 'flex',
                          gap: '12px',
                          flexWrap: 'wrap',
                          marginBottom: '10px',
                          fontSize: '13px',
                        }}
                      >
                        <span style={statusBadgeStyle('active')}>
                          {readyCount} ready
                        </span>
                        {skipCount > 0 && (
                          <span style={statusBadgeStyle('never')}>{skipCount} skipped</span>
                        )}
                        {errorCount > 0 && (
                          <span style={statusBadgeStyle('expired')}>{errorCount} errors</span>
                        )}
                        {readyCount > BULK_IMPORT_MAX && (
                          <span style={{ color: '#991b1b', fontWeight: 600 }}>
                            Over the {BULK_IMPORT_MAX}-per-batch limit. Split your list.
                          </span>
                        )}
                      </div>
                    );
                  })()}
                </div>
                <div
                  style={{
                    padding: '0 24px',
                    maxHeight: '50vh',
                    overflowY: 'auto',
                  }}
                >
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                    <thead style={{ background: '#f9fafb', position: 'sticky', top: 0 }}>
                      <tr>
                        <th style={thStyle}>Status</th>
                        <th style={thStyle}>First name</th>
                        <th style={thStyle}>Last name</th>
                        <th style={thStyle}>Email</th>
                        <th style={thStyle}>Note</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bulkImportParsed.map((p, i) => (
                        <tr key={`${p.email}:${i}`} style={{ borderTop: '1px solid #f3f4f6' }}>
                          <td style={tdStyle}>
                            <span
                              style={statusBadgeStyle(
                                p.status === 'ready'
                                  ? 'active'
                                  : p.status === 'skip'
                                  ? 'never'
                                  : 'expired'
                              )}
                            >
                              {p.status}
                            </span>
                          </td>
                          <td style={tdStyle}>{p.firstName || '—'}</td>
                          <td style={tdStyle}>{p.lastName || '—'}</td>
                          <td style={{ ...tdStyle, color: '#4b5563' }}>{p.email}</td>
                          <td style={{ ...tdStyle, color: '#6b7280' }}>{p.error || ''}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="modal-footer">
                  <button
                    className="btn-secondary"
                    onClick={() => setBulkImportStep('input')}
                  >
                    ← Back
                  </button>
                  <button
                    className="btn-primary"
                    onClick={runBulkImport}
                    disabled={
                      bulkImportParsed.filter((p) => p.status === 'ready').length === 0 ||
                      bulkImportParsed.filter((p) => p.status === 'ready').length > BULK_IMPORT_MAX
                    }
                  >
                    Grant Access to{' '}
                    {bulkImportParsed.filter((p) => p.status === 'ready').length} student
                    {bulkImportParsed.filter((p) => p.status === 'ready').length === 1 ? '' : 's'}
                  </button>
                </div>
              </>
            )}

            {bulkImportStep === 'importing' && (
              <div style={{ padding: '32px 24px', textAlign: 'center' }}>
                <p style={{ color: '#4b5563', marginBottom: '12px' }}>
                  Granting NCLEX access to{' '}
                  {bulkImportParsed.filter((p) => p.status === 'ready').length} students…
                </p>
                <p style={{ fontSize: '13px', color: '#9ca3af', marginTop: 0 }}>
                  Each student is processed sequentially with a short delay to stay within
                  Thinkific's rate limits. Please leave this window open until it finishes.
                </p>
              </div>
            )}

            {bulkImportStep === 'done' && (
              <>
                <div
                  style={{
                    padding: '0 24px',
                    maxHeight: '50vh',
                    overflowY: 'auto',
                  }}
                >
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                    <thead style={{ background: '#f9fafb', position: 'sticky', top: 0 }}>
                      <tr>
                        <th style={thStyle}>Status</th>
                        <th style={thStyle}>Name</th>
                        <th style={thStyle}>Email</th>
                        <th style={thStyle}>Note</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bulkImportResults.map((p, i) => (
                        <tr key={`${p.email}:${i}`} style={{ borderTop: '1px solid #f3f4f6' }}>
                          <td style={tdStyle}>
                            <span
                              style={statusBadgeStyle(
                                p.status === 'success'
                                  ? 'active'
                                  : p.status === 'skip'
                                  ? 'never'
                                  : 'expired'
                              )}
                            >
                              {p.status}
                            </span>
                          </td>
                          <td style={tdStyle}>
                            {[p.firstName, p.lastName].filter(Boolean).join(' ') || '—'}
                          </td>
                          <td style={{ ...tdStyle, color: '#4b5563' }}>{p.email}</td>
                          <td style={{ ...tdStyle, color: '#6b7280' }}>{p.error || ''}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="modal-footer">
                  <button className="btn-secondary" onClick={resetBulkImport}>
                    Import another batch
                  </button>
                  <button className="btn-primary" onClick={closeBulkImport}>
                    Done
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Backfill confirm modal */}
      {confirmBackfill && (
        <div className="modal-overlay" onClick={() => setConfirmBackfill(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>💳 Backfill Paid Status</h3>
              <button className="modal-close" onClick={() => setConfirmBackfill(false)}>
                ×
              </button>
            </div>
            <div style={{ padding: '16px 24px' }}>
              <p>
                Run a one-time backfill that scans every historical NCLEX order on Thinkific and
                marks each buyer as <strong>💳 Paid</strong>. Webhooks already track new orders in
                real-time, so you only need to do this once to capture pre-webhook history.
              </p>
              <p style={{ fontSize: '13px', color: '#6b7280', marginTop: '8px' }}>
                This runs as a background job and may take several minutes depending on order
                history. You can close this modal and keep working — when it finishes, refresh
                the page to see updated Paid counts. Progress and the final result land in the
                Audit Log.
              </p>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setConfirmBackfill(false)}
              >
                Cancel
              </button>
              <button type="button" className="btn-primary" onClick={doBackfill}>
                Start Backfill
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Audit log modal */}
      {showAuditLog && (
        <div className="modal-overlay" onClick={() => setShowAuditLog(false)}>
          <div
            className="modal"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: '900px', width: '90vw' }}
          >
            <div className="modal-header">
              <h3>Thinkific Audit Log</h3>
              <button className="modal-close" onClick={() => setShowAuditLog(false)}>
                ×
              </button>
            </div>
            <div style={{ padding: '8px 16px 16px', maxHeight: '60vh', overflowY: 'auto' }}>
              {auditLoading && (
                <div style={{ padding: '24px', textAlign: 'center', color: '#6b7280' }}>
                  Loading…
                </div>
              )}
              {!auditLoading && auditEntries.length === 0 && (
                <div style={{ padding: '24px', textAlign: 'center', color: '#9ca3af' }}>
                  No audit entries yet.
                </div>
              )}
              {!auditLoading && auditEntries.length > 0 && (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                  <thead style={{ background: '#f9fafb', position: 'sticky', top: 0 }}>
                    <tr>
                      <th style={thStyle}>When</th>
                      <th style={thStyle}>Admin</th>
                      <th style={thStyle}>Action</th>
                      <th style={thStyle}>Student</th>
                      <th style={thStyle}>Result</th>
                    </tr>
                  </thead>
                  <tbody>
                    {auditEntries.map((e) => (
                      <tr key={e.id} style={{ borderTop: '1px solid #f3f4f6' }}>
                        <td style={tdStyle}>{formatTime(e.created_at)}</td>
                        <td style={tdStyle}>
                          {e.actor?.name || e.actor_email || '—'}
                        </td>
                        <td style={tdStyle}>{e.action}</td>
                        <td style={tdStyle}>
                          {e.student?.name || e.student_email}
                        </td>
                        <td style={tdStyle}>
                          <span
                            style={{
                              ...statusBadgeStyle(
                                e.result === 'success' ? 'active' : e.result === 'partial' ? 'never' : 'expired'
                              ),
                            }}
                          >
                            {e.result}
                          </span>
                          {e.error_message && (
                            <div style={{ color: '#991b1b', fontSize: '12px', marginTop: '4px' }}>
                              {e.error_message}
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowAuditLog(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const thStyle = {
  textAlign: 'left',
  padding: '10px 14px',
  fontSize: '12px',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  color: '#6b7280',
};

const tdStyle = {
  padding: '12px 14px',
  fontSize: '14px',
  verticalAlign: 'middle',
};

// Pinned Stripe checkout link the admin uses to invoice students for a
// 45-day extension. Hardcoded for now - can be promoted to a settings
// table or env var later if it ever changes.
const STRIPE_EXTENSION_URL = 'https://buy.stripe.com/eVabJC9qk0xr3K028a';

// Stripe brand purple. Used for the Send Payment Link button so it's
// visually distinct from Grant/Revoke and signals "this is the
// paid extension flow".
const stripeButtonStyle = {
  background: '#635BFF',
  color: '#fff',
  border: 'none',
  borderRadius: '6px',
  padding: '8px 14px',
  fontSize: '13px',
  fontWeight: 600,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
};

const baseTinyBadge = {
  display: 'inline-block',
  padding: '1px 8px',
  borderRadius: '999px',
  fontSize: '11px',
  fontWeight: 600,
  lineHeight: '18px',
  whiteSpace: 'nowrap',
};

// Stripe-tinted "Paid" pill - intentionally similar hue to the Send
// Payment Link button so the link between purchase + payment flow is
// visually obvious.
const paidBadgeStyle = {
  ...baseTinyBadge,
  background: '#ede9fe',
  color: '#5b21b6',
};

const freeBadgeStyle = {
  ...baseTinyBadge,
  background: '#f3f4f6',
  color: '#6b7280',
};

const detailLabelStyle = {
  color: '#6b7280',
  fontWeight: 500,
  margin: 0,
};

const detailValueStyle = {
  color: '#111827',
  margin: 0,
};

export default ThinkificEnrollment;
